import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { cn } from '@/lib/utils';
import type { CarouselPrize, SpinResult } from '@/lib/carousel-service';
import {
  playCardFlip,
  playRiffle,
  playThump,
  playTick,
  playWinnerFlip,
  startRiser,
  stopRiser,
} from '@/lib/carousel-sound';
import { Gift, Sparkles } from 'lucide-react';

export type DeckPhase = 'idle' | 'shuffling' | 'flipping' | 'revealed';

interface PrizeCardDeckProps {
  prizes: CarouselPrize[];
  result: SpinResult | null;
  phase: DeckPhase;
  /** Total shuffle duration, used to pace the build-up. */
  shuffleMs?: number;
  onRevealComplete?: () => void;
}

/**
 * Build-up stages as a fraction of the shuffle, each faster than the last.
 * The final stage stops the riffle and trembles the stack for the "here it
 * comes" beat right before the reveal.
 */
const STAGES = [
  { from: 0, riffleDur: 1.5, cadence: 1500 },
  { from: 0.28, riffleDur: 1.1, cadence: 1100 },
  { from: 0.55, riffleDur: 0.78, cadence: 780 },
  { from: 0.78, riffleDur: 0.55, cadence: 550 },
  { from: 0.92, riffleDur: 0.55, cadence: 0 }, // tremble, no riffle
] as const;

const CARD_COUNT = 7;
const CENTER_INDEX = Math.floor(CARD_COUNT / 2);

/** Reveal choreography, in ms from the start of the `flipping` phase.
 *  The deck first transitions back out of its shuffled stack into the fan,
 *  then the losing cards turn, then the winner. */
const REVEAL_DECOYS_MS = 1180;
const REVEAL_WINNER_MS = 1600;
const REVEAL_DONE_MS = 2150;

/** 0 = deck regrouping, 1 = decoys face-up, 2 = winner revealed. */
type RevealStep = 0 | 1 | 2;

type DeckCard = {
  key: string;
  name: string;
  image_url: string | null;
  is_big_prize: boolean;
};

export function PrizeCardDeck({
  prizes,
  result,
  phase,
  shuffleMs = 12_000,
  onRevealComplete,
}: PrizeCardDeckProps) {
  const [revealStep, setRevealStep] = useState<RevealStep>(0);
  const [stage, setStage] = useState(0);

  const cards = useMemo<DeckCard[]>(() => {
    const source =
      prizes.length > 0
        ? prizes
        : Array.from({ length: CARD_COUNT }).map((_, i) => ({
            id: `placeholder-${i}`,
            name: 'Ödül',
            image_url: null as string | null,
            is_big_prize: false,
          }));

    return Array.from({ length: CARD_COUNT }).map((_, index) => {
      // Stride by a co-prime-ish step so neighbouring cards rarely repeat.
      const prize = source[(index * 3) % source.length];
      return {
        key: `slot-${index}`,
        name: prize.name,
        image_url: prize.image_url,
        is_big_prize: prize.is_big_prize,
      };
    });
  }, [prizes]);

  useEffect(() => {
    if (phase === 'revealed') {
      setRevealStep(2);
      return;
    }

    if (phase !== 'flipping') {
      setRevealStep(0);
      return;
    }

    setRevealStep(0);
    const timers = [
      window.setTimeout(() => {
        setRevealStep(1);
        // One flip per losing pair, matching the CSS transition stagger below.
        [0, 90, 180].forEach((offset) => playCardFlip(offset / 1000));
      }, REVEAL_DECOYS_MS),
      window.setTimeout(() => {
        setRevealStep(2);
        playWinnerFlip();
      }, REVEAL_WINNER_MS),
      window.setTimeout(() => onRevealComplete?.(), REVEAL_DONE_MS),
    ];

    return () => timers.forEach(window.clearTimeout);
  }, [phase, onRevealComplete]);

  /**
   * Drives the build-up: steps through STAGES over the shuffle, speeding up the
   * deck and the sound together, and launching the riser for the final climb.
   */
  useEffect(() => {
    if (phase !== 'shuffling') {
      setStage(0);
      stopRiser();
      return;
    }

    const started = Date.now();
    let currentStage = -1;
    let riserStarted = false;
    let nextRiffleAt = 0;

    const step = () => {
      const elapsed = Date.now() - started;
      const progress = Math.min(1, elapsed / shuffleMs);

      let index = 0;
      for (let i = 0; i < STAGES.length; i++) {
        if (progress >= STAGES[i].from) index = i;
      }

      if (index !== currentStage) {
        currentStage = index;
        setStage(index);
        // Each new gear gets a thump so the escalation is felt, not just seen.
        if (index > 0) playThump(0, 0.16 + index * 0.03);
      }

      const { cadence } = STAGES[index];
      if (cadence > 0 && elapsed >= nextRiffleAt) {
        playRiffle(Math.min(3, index));
        nextRiffleAt = elapsed + cadence;
      }
      // Ticks fill the gaps once things get quick.
      if (index >= 2 && cadence > 0) playTick(0, 0.05);
      if (index >= 4) playTick(0, 0.09);

      // Riser covers the last ~2.5s of the shuffle.
      const riserLead = 2.5;
      if (!riserStarted && elapsed >= shuffleMs - riserLead * 1000) {
        riserStarted = true;
        startRiser(riserLead);
      }
    };

    step();
    const id = window.setInterval(step, 160);
    return () => {
      window.clearInterval(id);
      stopRiser();
    };
  }, [phase, shuffleMs]);

  const isShuffling = phase === 'shuffling';
  const isRevealing = phase === 'flipping' || phase === 'revealed';
  const isTrembling = isShuffling && stage >= STAGES.length - 1;

  return (
    <div className="carousel-stage relative mx-auto flex w-full max-w-5xl flex-1 items-center justify-center">
      {/* Stage lighting — brightens stage by stage so the tension is visible
          as well as audible. Opacity only, to stay cheap on the TV. */}
      <div
        className="pointer-events-none absolute inset-0 transition-opacity duration-700"
        style={{ opacity: isShuffling ? 0.45 + stage * 0.14 : 0 }}
      >
        <div className="carousel-beam absolute left-1/4 top-[-30%] h-[160%] w-24 rotate-[8deg] bg-gradient-to-b from-amber-300/0 via-amber-300/25 to-amber-300/0 blur-2xl" />
        <div
          className="carousel-beam absolute right-1/4 top-[-30%] h-[160%] w-24 -rotate-[10deg] bg-gradient-to-b from-sky-300/0 via-sky-300/25 to-sky-300/0 blur-2xl"
          style={{ animationDelay: '-1.2s' }}
        />
        <div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-amber-400/30 blur-3xl transition-opacity duration-500"
          style={{
            height: 'calc(var(--card-h) * 1.6)',
            width: 'calc(var(--card-h) * 1.6)',
            opacity: 0.25 + stage * 0.18,
          }}
        />
      </div>

      {/* Halo behind the winning card */}
      <div
        style={{ height: 'calc(var(--card-h) * 1.5)', width: 'calc(var(--card-h) * 1.5)' }}
        className={cn(
          'carousel-winner-halo pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-amber-400/35 blur-3xl transition-opacity duration-700',
          revealStep >= 2 ? 'opacity-100' : 'opacity-0'
        )}
      />

      {/* Floor reflection so the deck is grounded instead of hovering in a void */}
      <div className="pointer-events-none absolute bottom-6 left-1/2 h-10 w-[70%] max-w-md -translate-x-1/2 rounded-[50%] bg-slate-950/50 blur-2xl" />

      <div className="carousel-deck w-full">
        {cards.map((card, index) => {
          const offset = index - CENTER_INDEX;
          const isCenter = index === CENTER_INDEX;
          const isWinner = isCenter && revealStep >= 2 && !!result;
          const isDecoy = !isCenter && revealStep >= 1;

          // Alternate the riffle arc side, and let outer cards travel further.
          const direction = (index % 2 === 0 ? 1 : -1) * (0.62 + Math.abs(offset) * 0.16);

          const style: CSSProperties = {
            ['--o' as string]: offset,
            ['--i' as string]: index,
            ['--dir' as string]: direction.toFixed(2),
            ['--riffle-dur' as string]: `${STAGES[stage].riffleDur}s`,
            zIndex: isWinner ? 40 : 10 + (CARD_COUNT - Math.abs(offset)),
          };

          return (
            <div
              key={card.key}
              style={style}
              className={cn(
                'carousel-card',
                isShuffling && 'is-stacked',
                isDecoy && 'is-dimmed',
                isWinner && 'is-winner'
              )}
            >
              <div
                className={cn(
                  'carousel-card-motion',
                  isShuffling && !isTrembling && 'is-riffling',
                  isTrembling && 'is-trembling',
                  !isShuffling && !isRevealing && 'is-floating'
                )}
              >
                <div
                  className={cn(
                    'carousel-card-inner rounded-2xl',
                    (isWinner || isDecoy) && 'carousel-card-flipped'
                  )}
                  style={{
                    // Outer cards turn first, the winner last — builds the beat.
                    transitionDelay: isDecoy && !isWinner ? `${(3 - Math.abs(offset)) * 90}ms` : '0ms',
                    boxShadow: isWinner
                      ? '0 30px 60px -12px rgba(0,0,0,0.7), 0 0 0 2px rgba(251,191,36,0.9), 0 0 40px rgba(251,191,36,0.5)'
                      : '0 18px 40px -12px rgba(0,0,0,0.65)',
                  }}
                >
                  {/* Back */}
                  <div className="carousel-card-face carousel-card-back rounded-2xl bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 ring-1 ring-inset ring-amber-300/30">
                    <div className="carousel-card-foil absolute inset-0 opacity-60" />
                    <div className="absolute inset-[6px] rounded-xl border border-amber-300/25" />
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_38%,rgba(251,191,36,0.28),transparent_62%)]" />
                    <div className="relative flex h-full flex-col items-center justify-center gap-2 text-amber-100">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-amber-300 to-amber-600 shadow-lg shadow-amber-900/50 sm:h-16 sm:w-16">
                        <Gift className="h-6 w-6 text-slate-900 sm:h-8 sm:w-8" />
                      </div>
                      <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-amber-200/80">
                        Ödül
                      </span>
                    </div>
                    <div className="carousel-card-sheen" />
                  </div>

                  {/* Front */}
                  <div className="carousel-card-face carousel-card-front rounded-2xl bg-white ring-1 ring-inset ring-white/70">
                    <PrizeFace
                      name={isWinner && result ? result.prize_name : card.name}
                      imageUrl={isWinner && result ? result.image_url : card.image_url}
                      isBigPrize={isWinner && result ? result.is_big_prize : card.is_big_prize}
                      highlight={isWinner}
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface PrizeFaceProps {
  name: string;
  imageUrl: string | null;
  isBigPrize: boolean;
  highlight: boolean;
}

function PrizeFace({ name, imageUrl, isBigPrize, highlight }: PrizeFaceProps) {
  return (
    <div className="flex h-full flex-col">
      {/* object-contain: prize photos have their own framing and must not be cropped. */}
      <div className="relative flex-1 overflow-hidden bg-white">
        {imageUrl ? (
          <img src={imageUrl} alt={name} className="h-full w-full object-contain p-2" />
        ) : (
          <div className="flex h-full items-center justify-center bg-gradient-to-br from-amber-50 to-rose-100">
            <Gift className="h-12 w-12 text-amber-500" />
          </div>
        )}
        {isBigPrize && (
          <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-400 to-amber-600 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-slate-900 shadow">
            <Sparkles className="h-3 w-3" />
            Büyük
          </span>
        )}
      </div>
      <div
        className={cn(
          'px-2 py-2.5 text-center',
          highlight ? 'bg-gradient-to-r from-amber-400 to-amber-500' : 'bg-white'
        )}
      >
        <p
          className={cn(
            'line-clamp-2 text-xs font-bold leading-snug sm:text-sm',
            highlight ? 'text-slate-900' : 'text-slate-700'
          )}
        >
          {name}
        </p>
      </div>
    </div>
  );
}
